import {
  AGGREGATION_METHODS,
  CALCULATION_METHODS,
  EF_BASES,
  EMISSION_SCOPES,
  EVIDENCE_KINDS,
  FREQUENCIES,
  KPI_CATEGORIES,
  KPI_STATUSES,
  QUESTION_TYPES,
  TAG_FIELDS,
} from "../options";
import {
  deriveFactorValue,
  factorsToWire,
  hasGasBreakdown,
  newFactor,
  keywordTypeFor,
  keywordValuesFor,
  isPatchEmpty,
  kpiTypeOptionsFor,
  newDraftRow,
  patchField,
  setTagField,
  supportsAggregation,
  validateFactors,
  validateUnits,
} from "../helpers";
import {
  FACTOR_COLUMNS,
  KPI_COLUMNS,
  SHEET,
  optionValue,
  parseFreeList,
  parseOptionList,
  parseUnits,
  parseYesNo,
  unitIdFor,
} from "./excelSpec";

const norm = (text) => String(text ?? "").trim().toLowerCase();

/** Fields compared between the sheet and the stored KPI, in patch order (type before aggregation). */
const FIELDS = [
  "title",
  "moduleId",
  "type",
  "aggregation",
  "frequency",
  "category",
  "defaultDescription",
  "yearDescription",
  "isReadOnly",
  "applicableCheck",
  "applicableNote",
  "trigerCheck",
  "units",
  "tagGroups",
  "evidences",
  "kpiTypes",
  "industryIds",
  "frameworkIds",
  "sectionMapping",
  "formula",
  "variables",
  "kpiStatus",
  "active",
];

const TAG_COLUMNS = KPI_COLUMNS.filter((c) => c.tagGroup);

/**
 * Stored KPIs the factor sheet points at, by ID or by title — the ones whose
 * current factors must be loaded before planning.
 * @param {{ factors: { lines: object[] } }} read from readWorkbook
 * @param {object[]} existing normalised rows
 * @returns {number[]}
 */
export const referencedKpiIds = (read, existing) => {
  const byTitle = new Map(existing.map((row) => [norm(row.title), row.questionId]));
  const ids = new Set();
  read.factors.lines.forEach(({ cells }) => {
    const id = Number(cells.kpiId);
    if (cells.kpiId && Number.isInteger(id)) ids.add(id);
    else if (byTitle.has(norm(cells.kpiTitle))) ids.add(byTitle.get(norm(cells.kpiTitle)));
  });
  return [...ids];
};

/**
 * Turns an uploaded workbook into a save plan against what is stored, without
 * saving anything. Every problem is collected with its sheet and row, and the
 * plan is only safe to apply when `errors` is empty.
 *
 * Rules:
 * - KPI ID blank is a new KPI; its title must not already exist.
 * - KPI ID filled updates that KPI. A column missing from the sheet leaves
 *   that field alone; a blank cell clears it (required fields excepted).
 * - Tags outside the fixed Emission / Energy / Module groups are kept.
 * - A fixed tag value with no `riu_keywords` pair yet is listed in
 *   `newKeywords`, so the upload can file it like the grid's "Add pair" does.
 * - A factor row with a Factor ID updates that factor, or removes it with
 *   Delete = Yes. Without one it is added. Factors not in the sheet stay.
 *
 * @param {{ read: object, existing: object[], factorsByKpi: Record<string, object[]>, catalogue: object }} args
 */
export const planImport = ({ read, existing, factorsByKpi, catalogue }) => {
  const errors = read.problems.map((message) => ({ sheet: "", line: null, message }));
  const creates = [];
  const updates = [];
  let unchanged = 0;

  const existingById = new Map(existing.map((row) => [String(row.questionId), row]));
  const existingByTitle = new Map(existing.map((row) => [norm(row.title), row]));
  const seenIds = new Set();
  const seenTitles = new Map();
  const unitsByKey = new Map();
  const newKeywords = new Map();
  const vocabulary = catalogue.tagValuesByKey || {};

  read.kpis.lines.forEach(({ line, cells }) => {
    const lineErrors = [];
    const fail = (message) => lineErrors.push(message);
    const has = (key) => read.kpis.present.has(key);

    let base;
    if (cells.id) {
      base = existingById.get(String(Number(cells.id)));
      if (!base) {
        errors.push({ sheet: SHEET.kpis, line, message: `KPI ID ${cells.id} is not in this financial year` });
        return;
      }
      if (seenIds.has(base.questionId)) fail(`KPI ID ${cells.id} appears more than once`);
      seenIds.add(base.questionId);
    } else {
      base = newDraftRow("", {});
    }
    const isNew = !cells.id;
    const values = { ...base };

    if (has("title")) values.title = cells.title;
    if (!String(values.title).trim()) fail("KPI title is required");

    const pick = (key, options, field, label) => {
      if (!has(key)) {
        if (isNew) fail(`${label} is required`);
        return;
      }
      if (!cells[key]) return fail(`${label} is required`);
      const value = optionValue(options, cells[key]);
      if (value == null) return fail(`Unknown ${label.toLowerCase()} "${cells[key]}"`);
      values[field] = value;
    };
    pick("module", catalogue.modules, "moduleId", "Module");
    pick("type", QUESTION_TYPES, "type", "Type");
    pick("frequency", FREQUENCIES, "frequency", "Frequency");

    if (has("aggregation") && cells.aggregation) {
      const value = optionValue(AGGREGATION_METHODS, cells.aggregation);
      if (value == null) fail(`Unknown aggregation "${cells.aggregation}"`);
      else values.aggregation = value;
    }

    if (!supportsAggregation(values.type)) values.aggregation = values.type === base.type ? base.aggregation : "";
    else if (!values.aggregation || (has("aggregation") && !cells.aggregation)) {
      values.aggregation = base.aggregation || "SUM";
    }

    if (has("units")) {
      const parsed = parseUnits(cells.units, catalogue.units, base.units.map((u) => u.unitId));
      if (parsed.error) fail(`Supported units: ${parsed.error}`);
      else values.units = parsed.units;
    }
    const unitError = validateUnits(values.units);
    if (unitError) fail(`Supported units: ${unitError}`);

    TAG_COLUMNS.forEach((c) => {
      if (!has(c.key)) return;
      values.tagGroups = setTagField(values.tagGroups, c.tagGroup, c.tagIndex, cells[c.key]);

      const value = String(cells[c.key] ?? "").trim();
      if (!value) return;
      const field = TAG_FIELDS[c.tagIndex].key;
      const known = keywordValuesFor(vocabulary, field).some((v) => norm(v) === norm(value));
      const type = keywordTypeFor(vocabulary, field);
      if (!known) newKeywords.set(`${type}|${norm(value)}`, { keywordType: type, keywordValue: value });
    });

    if (has("evidences")) values.evidences = parseFreeList(cells.evidences, [...EVIDENCE_KINDS, ...base.evidences]);

    if (has("category")) {
      const category = cells.category ? optionValue(KPI_CATEGORIES, cells.category) : "";
      if (category == null) fail(`Unknown category "${cells.category}"`);
      else values.category = category;
    }
    if (has("defaultDescription")) values.defaultDescription = cells.defaultDescription;
    if (has("yearDescription")) values.yearDescription = cells.yearDescription;

    const pickYesNo = (key, field, label) => {
      if (!has(key)) return;
      const answer = parseYesNo(cells[key]);
      if (answer === undefined) fail(`${label} must be Yes or No, not "${cells[key]}"`);
      else if (answer !== null) values[field] = answer;
    };
    pickYesNo("readOnly", "isReadOnly", "Read only");
    pickYesNo("applicableCheck", "applicableCheck", "Applicable check");
    pickYesNo("applicableNote", "applicableNote", "Applicable note");
    pickYesNo("trigerCheck", "trigerCheck", "Trigger check");

    const pickMany = (key, options, field, label) => {
      if (!has(key)) return;
      const parsed = parseOptionList(cells[key], options);
      if (parsed.error) fail(`${label}: ${parsed.error}`);
      else values[field] = parsed.values;
    };
    pickMany("kpiType", kpiTypeOptionsFor(base.kpiTypes), "kpiTypes", "KPI type");
    pickMany("industries", catalogue.industries || [], "industryIds", "Industries");
    pickMany("frameworks", catalogue.frameworks || [], "frameworkIds", "Frameworks");

    if (has("sectionMapping")) values.sectionMapping = cells.sectionMapping;
    if (has("formula")) values.formula = cells.formula;
    if (has("variables")) {
      values.variables = String(cells.variables ?? "")
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean);
    }
    if (String(values.formula || "").trim() && !values.variables.length) {
      fail("A formula needs its variables");
    }

    if (has("kpiStatus") && cells.kpiStatus) {
      const status = optionValue(KPI_STATUSES, cells.kpiStatus);
      if (status == null) fail(`Unknown KPI status "${cells.kpiStatus}"`);
      else values.kpiStatus = status;
    }

    if (has("active")) {
      const active = parseYesNo(cells.active);
      if (active === undefined) fail(`Active must be Yes or No, not "${cells.active}"`);
      else if (active !== null) values.active = active;
    }

    const titleKey = norm(values.title);
    if (titleKey) {
      if (seenTitles.has(titleKey)) fail(`Same title as row ${seenTitles.get(titleKey)}`);
      seenTitles.set(titleKey, line);
      const holder = existingByTitle.get(titleKey);
      if (holder && holder.questionId !== base.questionId) {
        fail(
          isNew
            ? `"${values.title}" already exists (KPI ID ${holder.questionId}) — put its ID in the KPI ID column to update it`
            : `Another KPI (ID ${holder.questionId}) already uses the title "${values.title}"`,
        );
      }
    }

    if (lineErrors.length) {
      lineErrors.forEach((message) => errors.push({ sheet: SHEET.kpis, line, message }));
      return;
    }

    values.title = String(values.title).trim();
    unitsByKey.set(isNew ? `new:${titleKey}` : `id:${base.questionId}`, values.units);
    if (isNew) {
      creates.push({ line, values });
      return;
    }
    let patch = {};
    FIELDS.forEach((field) => {
      patch = patchField(base, patch, field, values[field]);
    });
    if (isPatchEmpty(patch)) unchanged += 1;
    else updates.push({ line, row: base, patch, fields: Object.keys(patch) });
  });

  const factorPlan = planFactors({
    read,
    existingById,
    existingByTitle,
    creates,
    factorsByKpi,
    unitsByKey,
    catalogue,
    errors,
  });

  return {
    errors,
    creates,
    updates,
    unchanged,
    factorItems: factorPlan,
    newKeywords: [...newKeywords.values()],
    hasChanges: creates.length + updates.length + factorPlan.length > 0,
  };
};


const planFactors = ({ read, existingById, existingByTitle, creates, factorsByKpi, unitsByKey, catalogue, errors }) => {
  const createsByTitle = new Map(creates.map((c) => [norm(c.values.title), c.values]));
  const lists = new Map();

  read.factors.lines.forEach(({ line, cells }) => {
    const lineErrors = [];
    const fail = (message) => lineErrors.push(message);

    let key;
    let target;
    if (cells.kpiId) {
      const row = existingById.get(String(Number(cells.kpiId)));
      if (!row) {
        errors.push({ sheet: SHEET.factors, line, message: `KPI ID ${cells.kpiId} is not in this financial year` });
        return;
      }
      key = `id:${row.questionId}`;
      target = { questionId: row.questionId, title: row.title, units: row.units };
    } else if (createsByTitle.has(norm(cells.kpiTitle))) {
      const values = createsByTitle.get(norm(cells.kpiTitle));
      key = `new:${norm(values.title)}`;
      target = { questionId: null, title: values.title, units: values.units };
    } else if (existingByTitle.has(norm(cells.kpiTitle))) {
      const row = existingByTitle.get(norm(cells.kpiTitle));
      key = `id:${row.questionId}`;
      target = { questionId: row.questionId, title: row.title, units: row.units };
    } else {
      errors.push({
        sheet: SHEET.factors,
        line,
        message: cells.kpiTitle
          ? `No KPI titled "${cells.kpiTitle}" in this file or this financial year`
          : "Give the KPI ID or KPI title this factor belongs to",
      });
      return;
    }

    if (!lists.has(key)) {
      const before = target.questionId == null ? [] : factorsByKpi[String(target.questionId)] || [];
      lists.set(key, { ...target, before, factors: [...before], lines: [] });
    }
    const entry = lists.get(key);
    entry.lines.push(line);

    const remove = parseYesNo(cells.delete);
    if (remove === undefined) fail(`Delete must be Yes or No, not "${cells.delete}"`);

    let index = -1;
    if (cells.factorId) {
      index = entry.factors.findIndex((f) => String(f.id) === String(Number(cells.factorId)));
      if (index === -1) fail(`Factor ID ${cells.factorId} does not belong to "${entry.title}"`);
    }

    if (remove) {
      if (!lineErrors.length && index > -1) entry.factors.splice(index, 1);
      lineErrors.forEach((message) => errors.push({ sheet: SHEET.factors, line, message }));
      return;
    }

    const scope = optionValue(EMISSION_SCOPES, cells.scope);
    if (!scope) fail(cells.scope ? `Unknown scope "${cells.scope}"` : "Scope is required");
    const countryCode = String(cells.country || "").trim().toUpperCase();
    if (!countryCode) fail("Country is required");
    const preferIds = (unitsByKey.get(key) || target.units || []).map((u) => u.unitId);
    const unitId = unitIdFor(catalogue.units, cells.unit, preferIds);
    if (unitId == null) fail(cells.unit ? `Unknown unit "${cells.unit}"` : "Per unit is required");
    const databaseId = optionValue(catalogue.databases, cells.source);
    if (databaseId == null) fail(cells.source ? `Unknown source "${cells.source}"` : "Source is required");
    const breakdown = breakdownFields(cells, read.factors.present, fail);

    const base = index > -1 ? entry.factors[index] : newFactor();
    const unit = catalogue.units.find((option) => Number(option.value) === unitId)?.label || "";
    const merged = { ...base, ...breakdown, scope, countryCode, unitId, unit, value: cells.value, databaseId: String(databaseId), databaseName: "" };
    if (hasGasBreakdown(merged)) {
      const derived = deriveFactorValue(merged);
      if (isHandTypedValue(cells.value, base.value, derived)) {
        fail(`Value ${cells.value} differs from ${derived} calculated from the gas factors; leave Value as downloaded or blank, or change the gas columns`);
      }
      merged.value = String(derived);
    } else {
      const value = Number(cells.value);
      if (cells.value === "" || !Number.isFinite(value) || value < 0) fail("Value must be a number of zero or more");
    }

    if (lineErrors.length) {
      lineErrors.forEach((message) => errors.push({ sheet: SHEET.factors, line, message }));
      return;
    }

    if (index > -1) entry.factors[index] = merged;
    else entry.factors.push({ ...merged, id: null });
  });

  const items = [];
  lists.forEach((entry) => {
    const listError = validateFactors(entry.factors);
    if (listError) {
      errors.push({ sheet: SHEET.factors, line: entry.lines[0], message: `${entry.title}: ${listError}` });
      return;
    }
    const changed = JSON.stringify(factorsToWire(entry.factors)) !== JSON.stringify(factorsToWire(entry.before));
    if (changed) {
      items.push({
        questionId: entry.questionId,
        title: entry.title,
        factors: entry.factors,
        before: entry.before.length,
        after: entry.factors.length,
      });
    }
  });
  return items;
};

const VALUE_TOLERANCE = 1e-9;

/**
 * @param {number} a
 * @param {number} b
 * @returns {boolean} whether the two agree to the precision the sheet round trip keeps
 */
const sameNumber = (a, b) => Math.abs(a - b) <= VALUE_TOLERANCE * Math.max(1, Math.abs(b));

/**
 * Whether the Value cell of a gas-wise factor was edited to something the gas
 * columns do not give. A blank cell, or one still holding the downloaded value,
 * was not edited.
 *
 * @param {string} typed the Value cell
 * @param {string} stored the factor's value before this upload, "" for a new factor
 * @param {number} derived the value the gas columns give
 * @returns {boolean}
 */
const isHandTypedValue = (typed, stored, derived) => {
  if (String(typed ?? "").trim() === "") return false;
  const value = Number(typed);
  if (sameNumber(value, derived)) return false;
  return String(stored ?? "").trim() === "" || !sameNumber(value, Number(stored));
};

const BREAKDOWN_KEYS = FACTOR_COLUMNS.filter((column) => column.breakdown).map((column) => column.key);

/**
 * The gas-wise columns the sheet carries, as grid factor fields. A column the
 * sheet leaves out keeps the stored value, like every other column.
 *
 * @param {Record<string, string>} cells
 * @param {Set<string>} present
 * @param {(message: string) => void} fail
 * @returns {Partial<import("../helpers").GridFactor>}
 */
const breakdownFields = (cells, present, fail) => {
  const fields = {};
  BREAKDOWN_KEYS.filter((key) => present.has(key)).forEach((key) => {
    fields[key] = String(cells[key] ?? "").trim();
  });
  if (present.has("method")) {
    fields.method = cells.method ? optionValue(CALCULATION_METHODS, cells.method) : "DIRECT";
    if (!fields.method) fail(`Unknown method "${cells.method}"`);
  }
  if (present.has("basis")) {
    fields.basis = cells.basis ? optionValue(EF_BASES, cells.basis) : "GAS_MASS";
    if (!fields.basis) fail(`Unknown gas value basis "${cells.basis}"`);
  }
  return fields;
};
